import {
  Button,
  Coin,
  Flex,
  Icon,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  Stack,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';

/**
 * What became of a queued export, reported the way Ember's async-export modal
 * does: a status coin, a sentence, and a way on to the exports page — or, when
 * the request was refused, a way to try again.
 *
 * No heading, matching Ember, which passes the modal no titles for this flow;
 * the dialog is named for assistive tech instead.
 */

const QUEUED_ARIA_LABEL = 'Export to CSV';
const FAILED_MESSAGE = 'There was a problem processing your export. Try again.';

/**
 * What became of the export. Only a refused one can be retried, so only a
 * refused one carries the retry: a caller that reports refusals some other
 * way cannot be asked for a handler the modal would never call.
 */
export type ExportResultModalData = {
  /** The exports page the finished file will appear on. */
  exportsHref: string;
  /** Uses the wider, roomier layout of the legacy Analysis report export. */
  presentation?: 'analysis-report';
} & (
  | { outcome: 'queued' }
  | {
      outcome: 'failed';
      /**
       * Runs the export again. Owned by the page: a managed modal mounts
       * outside its providers, so it cannot reach the mutation itself.
       */
      onRetry: () => void;
    }
);

type ExportResultStatusProps = Pick<ExportResultModalData, 'outcome' | 'presentation'>;

function ExportResultStatus({ outcome, presentation }: ExportResultStatusProps) {
  const isQueued = outcome === 'queued';

  if (presentation !== 'analysis-report') {
    return (
      <Coin colorVariant={isQueued ? 'green' : 'red'} size="xl">
        <Icon name={isQueued ? 'check-circle' : 'critical'} />
      </Coin>
    );
  }

  return (
    <Flex justifyContent="center">
      <Flex
        alignItems="center"
        justifyContent="center"
        flexShrink="0"
        backgroundColor={isQueued ? 'success-minimal' : 'critical-minimal'}
        style={{
          width: themeVars.space['1000'],
          height: themeVars.space['1000'],
          borderRadius: themeVars.radius.labels,
        }}
        data-test-export-result-status={outcome}
      >
        <Icon name={isQueued ? 'check-circle' : 'critical'} color={isQueued ? 'success' : 'critical'} size="xl" />
      </Flex>
    </Flex>
  );
}

type ExportResultMessageProps = Pick<ExportResultModalData, 'outcome' | 'exportsHref' | 'presentation'>;

function ExportResultMessage({ outcome, exportsHref, presentation }: ExportResultMessageProps) {
  if (outcome === 'failed') {
    return (
      <Paragraph textAlign={presentation === 'analysis-report' ? 'left' : 'center'} data-test-export-summary={outcome}>
        {FAILED_MESSAGE}
      </Paragraph>
    );
  }

  return (
    <Paragraph textAlign={presentation === 'analysis-report' ? 'left' : 'center'} data-test-export-summary={outcome}>
      Your export is currently processing. When it is ready, you will receive an email, and it will be available for
      download on the{' '}
      <Link href={exportsHref} isUnderlined>
        exports page
      </Link>
      .
    </Paragraph>
  );
}

export const ExportResultModal = ({
  data,
  onConfirm,
  onClose,
  modalState,
}: PlumaManagedModalProps<ExportResultModalData>) => {
  const { outcome, exportsHref } = data;
  const isQueued = outcome === 'queued';
  const isAnalysisReport = data.presentation === 'analysis-report';

  return (
    <Modal
      state={modalState}
      onClose={onClose}
      shouldShowCloseButton={false}
      aria-label={QUEUED_ARIA_LABEL}
      data-test-export-result-modal={outcome}
    >
      <ModalBody>
        <Stack
          alignInline={isAnalysisReport ? 'stretch' : 'center'}
          gap={isAnalysisReport ? '300' : '200'}
          px={isAnalysisReport ? '400' : '300'}
          py={isAnalysisReport ? '400' : '300'}
        >
          <ExportResultStatus outcome={outcome} presentation={data.presentation} />
          <ExportResultMessage outcome={outcome} exportsHref={exportsHref} presentation={data.presentation} />
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={() => onClose()} data-test-export-result-button="close">
          Close
        </Button>
        {isQueued ? (
          <Button
            variant="primary"
            href={exportsHref}
            onClick={() => onClose()}
            data-test-export-result-button="exports"
          >
            Go to Exports
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={() => {
              onConfirm();
              if (data.outcome === 'failed') {
                data.onRetry();
              }
            }}
            data-test-export-result-button="retry"
          >
            Retry
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};
